"use client";

import { useEffect, useRef, useState } from "react";

// D-038 media Wave 2b (Jun 24, PASTE #160): sign-in left-panel video.
// Vertical 720x1280 H264 + AAC reuse of the Cayambe, Ecuador atmospheric film
// already on CloudFront for the homepage hero, transcoded to fit a
// portrait panel.  Tesla-pattern bottom-right mute / unmute button
// (same UX as the homepage hero) so users can opt in to audio.
//
// Mirrors components/marketing/HomeHeroVideo.tsx — kept as a separate
// component because the source URL + container constraints differ
// (vertical aspect, narrower object-cover target).

type Props = {
  ariaLabel: string;
  muteLabel: string;
  unmuteLabel: string;
};

export function SignInPanelVideo({
  ariaLabel,
  muteLabel,
  unmuteLabel,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = muted;
  }, [muted]);

  const handleToggle = (): void => {
    setMuted((prev) => !prev);
  };

  return (
    <>
      <video
        ref={videoRef}
        data-testid="sign-in-panel-video"
        aria-label={ariaLabel}
        poster="/poster/signin-hero.avif"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="absolute inset-0 h-full w-full object-cover"
        data-media-slot="sign-in-brand-bg"
      >
        {/* PASTE #203-B (final): the SAME live 720x1280 loop, stream-copy
            trimmed to 23s — identical quality (4.85 Mbps H264), just shorter.
            The 1080p re-encodes went soft once compressed to the §2.11
            budget, so per Alexis we keep this higher-bitrate 720p source. */}
        <source
          src="https://d154w98knb4f7r.cloudfront.net/videos/signin-loop-720x1280-23s.h264.mp4"
          type='video/mp4; codecs="avc1.42E01E"'
        />
      </video>
      <button
        type="button"
        data-testid="sign-in-panel-mute-toggle"
        aria-label={muted ? unmuteLabel : muteLabel}
        aria-pressed={!muted}
        onClick={handleToggle}
        className="group absolute bottom-6 right-6 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/80 backdrop-blur-sm transition-all hover:border-brand-teal/60 hover:bg-black/60 hover:text-brand-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/70 md:bottom-8 md:right-8"
      >
        {muted ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
            aria-hidden
          >
            <path d="M11 5L6 9H2v6h4l5 4V5z" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
            aria-hidden
          >
            <path d="M11 5L6 9H2v6h4l5 4V5z" />
            <path d="M15.54 8.46a5 5 0 010 7.07" />
            <path d="M19.07 4.93a10 10 0 010 14.14" />
          </svg>
        )}
      </button>
    </>
  );
}
